import React, { useEffect } from "react";
import { useNavigate } from "react-router-dom";
import { useTranslation } from "react-i18next";
import { MessageCircle, Camera, Video } from "lucide-react";
import { useChat } from "../../context/ChatContext";
import Loader from "../../components/common/Loader";
import EmptyState from "../../components/common/EmptyState";
import { color, radius, shadow } from "../../styles/theme";

/**
 * Conversation list — web port of mobile's `Screens/inbox`. Each row is one
 * event's chat room (regular event chat or an @Room), fed by
 * `Chat/GetMyChatRooms` (roomType omitted -> every conversation, see
 * ChatContext.loadRooms). Row click opens `/chat/:eventId`.
 */
const ChatInboxPage = () => {
  const { t } = useTranslation(["chat", "common"]);
  const navigate = useNavigate();
  const chat = useChat();

  useEffect(() => {
    chat?.loadRooms(1);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  if (!chat) return null;
  const { rooms, roomsLoading, roomsLoadingMore, roomsHasMore, loadMoreRooms } = chat;

  const openRoom = (room) => {
    const eventId = room.EventId ?? room.Id;
    if (eventId != null) navigate(`/chat/${eventId}`);
  };

  return (
    <div>
      <h2 style={s.heading}>{t("inbox.title")}</h2>

      {roomsLoading ? (
        <Loader />
      ) : rooms.length === 0 ? (
        <EmptyState icon={MessageCircle} text={t("inbox.empty")} />
      ) : (
        <div style={s.list}>
          {rooms.map((room) => {
            const lastMessage = room.LastMessage?.[0];
            const messageType = lastMessage?.MessageType;
            return (
              <div key={room.Id} className="row-link" style={s.row} onClick={() => openRoom(room)}>
                <div style={s.avatarWrap}>
                  {room.LogoUrl ? (
                    <img src={room.LogoUrl.replace("\\", "/")} alt="" style={s.avatar} onError={(e) => { e.target.style.display = "none"; }} />
                  ) : (
                    <div style={s.avatarFallback}>{room.Name?.charAt(0).toUpperCase() || "?"}</div>
                  )}
                </div>

                <div style={s.info}>
                  <div style={s.topLine}>
                    <span style={s.name}>{room.Name}</span>
                    {lastMessage?.Timestamp && <span style={s.time}>{new Date(lastMessage.Timestamp).toLocaleDateString(undefined, { month: "short", day: "numeric" })}</span>}
                  </div>
                  <div style={s.preview}>
                    {messageType === "image" ? (
                      <><Camera size={12} strokeWidth={2} /> {t("inbox.previewPhoto")}</>
                    ) : messageType === "video" ? (
                      <><Video size={12} strokeWidth={2} /> {t("inbox.previewVideo")}</>
                    ) : (
                      lastMessage?.Message || ""
                    )}
                  </div>
                </div>

                {room.UnreadMessageCount > 0 && <span style={s.unreadBadge}>{room.UnreadMessageCount}</span>}
              </div>
            );
          })}
        </div>
      )}

      {roomsHasMore && (
        <div style={{ textAlign: "center", marginTop: 14 }}>
          <button type="button" className="btn btn-secondary" onClick={loadMoreRooms} disabled={roomsLoadingMore}>
            {roomsLoadingMore ? t("common:status.loading") : t("inbox.loadMore")}
          </button>
        </div>
      )}
    </div>
  );
};

const s = {
  heading: { margin: "0 0 20px", fontSize: 21, fontWeight: 800, color: color.textPrimary, letterSpacing: -0.4 },
  list: { maxWidth: 640, background: color.surface, borderRadius: radius.lg, border: `1px solid ${color.border}`, boxShadow: shadow.xs, overflow: "hidden" },
  row: { display: "flex", alignItems: "center", gap: 14, padding: "14px 18px", cursor: "pointer", borderBottom: `1px solid ${color.gray100}` },
  avatarWrap: { flexShrink: 0 },
  avatar: { width: 44, height: 44, borderRadius: "50%", objectFit: "cover", display: "block" },
  avatarFallback: {
    width: 44, height: 44, borderRadius: "50%", background: color.primary500, color: "#fff",
    display: "flex", alignItems: "center", justifyContent: "center", fontWeight: 700, fontSize: 17,
  },
  info: { flex: 1, minWidth: 0 },
  topLine: { display: "flex", justifyContent: "space-between", alignItems: "center", gap: 8 },
  name: { fontWeight: 700, fontSize: 14, color: color.textPrimary, overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap" },
  time: { fontSize: 11, color: color.textMuted, flexShrink: 0 },
  preview: {
    display: "flex", alignItems: "center", gap: 5, fontSize: 12.5, color: color.textMuted, marginTop: 3,
    overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap",
  },
  unreadBadge: {
    background: color.primary500, color: "#fff", borderRadius: "50%", minWidth: 20, height: 20, padding: "0 5px",
    display: "flex", alignItems: "center", justifyContent: "center", fontSize: 11, fontWeight: 700, flexShrink: 0,
  },
};

export default ChatInboxPage;
